import { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TachographCompanyCardRow } from "../models/Tachograph";
import { formatUtcDateOrEmpty } from "../utilities/tachographDates";
import { TACHOGRAPH_PAGE_SIZES } from "./tachographGridDefaults";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { LoadFailedOverlay } from "@/components/grid/LoadFailedOverlay";
import { NoRowsOverlay } from "@/components/grid/NoRowsOverlay";
import { formatDate } from "@/hooks/metrics/useFormattedDate";

type Props = {
  rows: TachographCompanyCardRow[];
  loading: boolean;
  /** True while a source of the rows is loading or failed. */
  exportDisabled: boolean;
  /** True when a source of the rows failed: an empty grid then says so instead of "no rows". */
  loadFailed: boolean;
};

/** The company cards in use, with their validity. */
export function TachographCompanyCardGrid({ rows, loading, exportDisabled, loadFailed }: Props) {
  const { t, i18n } = useTranslation();

  const columns = useMemo<GridColDef<TachographCompanyCardRow>[]>(
    () => [
      {
        field: "cardNumber",
        headerName: t("tachograph.columns.cardNumber"),
        minWidth: 220,
        flex: 1,
      },
      {
        field: "companyName",
        headerName: t("tachograph.columns.companyName"),
        minWidth: 260,
        flex: 1,
      },
      { field: "icc", headerName: t("tachograph.columns.icc"), minWidth: 180, flex: 0.8 },
      { field: "status", headerName: t("tachograph.columns.status"), minWidth: 130, flex: 0.7 },
      { field: "source", headerName: t("tachograph.columns.source"), minWidth: 190, flex: 0.8 },
      {
        field: "validFrom",
        headerName: t("tachograph.columns.validFrom"),
        minWidth: 140,
        flex: 0.7,
        type: "date",
        valueGetter: (_value, row) => row.validFrom,
        valueFormatter: formatUtcDateOrEmpty,
      },
      {
        field: "expiryDateUtc",
        headerName: t("tachograph.columns.expiryDateUtc"),
        minWidth: 160,
        flex: 0.8,
        type: "date",
        valueGetter: (_value, row) => row.expiryDateUtc,
        valueFormatter: formatUtcDateOrEmpty,
      },
      {
        field: "lastUpdate",
        headerName: t("tachograph.columns.lastUpdate"),
        minWidth: 190,
        flex: 0.8,
        type: "date",
        valueGetter: (_value, row) => row.lastUpdate,
        valueFormatter: (value: Date | null) => formatDate(value ?? undefined, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  return (
    <I18nGrid
      rows={rows}
      columns={columns}
      loading={loading}
      pagination
      disableRowSelectionOnClick
      pageSizeOptions={TACHOGRAPH_PAGE_SIZES}
      slots={{
        noRowsOverlay: loadFailed ? LoadFailedOverlay : NoRowsOverlay,
        toolbar: ExportToolbar,
      }}
      slotProps={{
        toolbar: { report: { title: t("tachograph.tabs.companyCards") }, exportDisabled },
      }}
      settingsKey="tachographCompanyCards"
      initialState={{ density: "compact", sorting: { sortModel: [{ field: "cardNumber", sort: "asc" }] } }}
    />
  );
}
